import {AfterViewInit, ChangeDetectorRef, Component, HostBinding, inject, model, OnInit} from '@angular/core';
import {ProgressSpinner} from "primeng/progressspinner";
import {CookieService} from 'ngx-cookie-service';
import {CyloginService, LoginComponent} from '@auth';
import {APIService} from '@core/services';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService, Translation} from '@ngx-translate/core';
import {assets, PROJECTS} from '@core/constants';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {browserRefresh} from '@app/app';
import {NgComponentOutlet} from '@angular/common';
import {PatientComponent} from '@modules/cy/patient/pages/patient/patient.component';
import {decodeToken} from '@core/helpers';
import {environment} from '@env/environment';
import {Token} from '@core/classes/patient/token';
import {HttpHeaders} from '@angular/common/http';
import {TokenData} from '@core/classes/patient/token-data';
import {PatientService} from '@core/services/patient.service';
import {Demographics} from '@core/classes/patient/demographics';
import {PatientSession} from '@core/classes/other/patient-session';
import {Profile as PatientProfile} from '@core/classes/patient/profile';
import {PatientVersion} from '@core/classes/other/patient-version';

@UntilDestroy()
@Component({
  selector: 'ncp-patient-home',
  imports: [
    ProgressSpinner,
    NgComponentOutlet,
    PatientComponent
  ],
  templateUrl: './patient-home.component.html',
  styleUrl: './patient-home.component.scss',
})
export class PatientHomeComponent implements OnInit, AfterViewInit {
  @HostBinding('class.hasToken') hasToken: boolean | undefined = false;
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private cookieService = inject(CookieService);
  private cyLoginService = inject(CyloginService);
  private apiService = inject(APIService);
  protected patientService = inject(PatientService);
  private _isLoggingIn: boolean = false;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  protected translations!: Translation;
  protected readonly nationalSrc = assets.img.home.national.src;
  protected readonly europeanSrc = assets.img.home.european.src;
  protected readonly profileSrc = assets.img.home.profile.src;

  protected profile = model<PatientProfile>();
  protected token = model<Token>();

  get isLoggingIn() {
    return this._isLoggingIn;
  }

  set isLoggingIn(isLoggingIn: boolean) {
    this._isLoggingIn = isLoggingIn;
  }

  ngOnInit() {
    this.translations = this.translate.instant('home');
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('home');
    });

    if (JSON.parse(sessionStorage.getItem('patient')!)) {
      this.token.set({ token: (JSON.parse(sessionStorage.getItem('patient')!) as PatientSession).token});
      this.hasToken = (environment.project === PROJECTS.BENEFICIARIES);
      //this.changeDetectorRef.detectChanges();
    }

    this.patientService.init();

    //if(sessionStorage.getItem())

    if (browserRefresh()) {
      // For development only
      this.makeRequests(false);
    } else {
      this.route.queryParamMap.subscribe((params) => {
        const success = params.get('s');
        if (params.has('s') && params.get('s') === 'true') {
          this.isLoggingIn = true;
          this.makeRequests(true);
        } else {
          this.goToInitialPage();
        }
      });
    }
  }

  makeRequests = (hasParam: boolean) => {
    const subscription = this.cyLoginService
      .isAuthenticated()
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.apiService
            .callMany(
              [
                { path: '/token', name: 'token' },
              ],
              [
                {
                  path: 'logoutURL',
                  observable: this.cyLoginService.getSessionClaim('bff:logout_url'),
                },
              ]
            )
            ?.subscribe((res) => {
              if (res && res['token'] && res['token'] && res['logoutURL']) {
                console.log('Token = ' + (res['token'] as Token).pToken);
                const tokenData: TokenData = decodeToken((res['token'] as Token)?.pToken || '') as TokenData;
                this.token.set(res['token'] as Token);
                console.log('Token: ' + JSON.stringify(tokenData));

                //sessionStorage.setItem('token', JSON.stringify(res['token']));
                //sessionStorage.setItem('tokenData', JSON.stringify(tokenData));
                sessionStorage.setItem('profile', JSON.stringify({
                  name: tokenData.given_name + ' ' + tokenData.family_name
                }));
                sessionStorage.setItem('logout_url', res['logoutURL'] as string); // + '&returnUrl=/api/logged-out');

                if(tokenData && tokenData.pid && tokenData.pid >= 0) {
                  this.apiService
                    .callMany(
                      [
                        {
                          path: '/latested-version',
                          headers: new HttpHeaders({}).set('pToken', this.token()?.pToken || ''),
                          name: 'version'
                        },
                        {
                          path: '/get-demographics',
                          headers: new HttpHeaders({}).set('pToken', this.token()?.pToken || ''),
                          body: {},
                          name: 'demographics'
                        }
                      ]
                    )?.subscribe((rslt) => {
                    if (rslt && rslt['version'] && rslt['demographics']) {
                      console.log(rslt['version']);
                      console.log(rslt['demographics']);
                      let demographics: Demographics = rslt['demographics'] as Demographics;
                      let version: PatientVersion = rslt['version'] as PatientVersion;

                      let profile: PatientProfile = rslt['profile'] as PatientProfile;
                      this.patientService.patient = {
                        id: tokenData.pid,
                        token: this.token()?.pToken,
                        version: {
                          id: -1,
                          new: -1,
                          details: null,
                          latest_dt: version.v_dt,
                          latest_author: version.author,
                        },
                        data: {
                          address: demographics?.data?.address,
                          full_address: demographics?.data?.full_address,
                          country: demographics?.data?.country,
                          dob: demographics?.cdata?.dob,
                          email: demographics?.data?.email,
                          firstname: demographics?.cdata?.firstname,
                          gender: demographics?.cdata?.gender,
                          lastname: demographics?.cdata?.lastname,
                          nationalId: demographics?.cdata?.nationalId,
                          patientId: tokenData.pid,
                          phone: demographics?.data?.telephone,
                        },
                      };

                      this.hasToken = true;

                      if(hasParam) {
                        this.isLoggingIn = false;
                      }
                      this.changeDetectorRef.detectChanges();
                      subscription.unsubscribe();
                      this.goToInitialPage();
                    }
                  });
                } else {
                  this.hasToken = true;

                  this.patientService.patient = {
                    id: tokenData.pid,
                    token: this.token()?.pToken,
                    version: {
                      id: -1,
                      new: -1,
                      details: null,
                    },
                    data: {
                      dob: tokenData.birthdate,
                      firstname: tokenData.given_name,
                      lastname: tokenData.family_name,
                      nationalId: tokenData.unique_identifier,
                      patientId: tokenData.pid,
                      phone: tokenData.phone_number,
                    },
                  };

                  if(hasParam) {
                    this.isLoggingIn = false;
                  }
                  this.changeDetectorRef.detectChanges();
                  this.goToInitialPage();
                }
              }
            });
        } else {
          this.router.navigate(['/']);
        }
      });

  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  goToInitialPage(): void {
    if (this.token()) {
      this.router.navigate(['/']);
    } else {
      //this.profile.set(undefined);
      this.router.navigate(['/']);
    }
  }

  protected goToCY = () => {
    this.router.navigate(['cy']);
  };

  protected goToEU = () => {
    this.router.navigate(['eu']);
  };

  protected goToProfile = () => {};

  protected readonly sessionStorage = sessionStorage;
  protected readonly LoginComponent = LoginComponent;
}
